/**
 * Tema VibeTide - Design System Completo
 * Palette basata sul logo: blu vibrante con tocchi moderni
 */
import { createTheme } from '@mui/material/styles';

// Palette VibeTide
export const vibetideColors = {
  primary: '#1E88E5',
  primaryDark: '#1565C0',
  primaryLight: '#64B5F6',
  accent: '#00E5FF',
  dark: '#0D1B2A',
  darkBlue: '#1B263B',
  light: '#E3F2FD',
  white: '#FFFFFF',
  textDark: '#1A1A2E',
  textMuted: '#5C6B7A',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',
  gradient: {
    primary: 'linear-gradient(135deg, #1E88E5 0%, #1565C0 100%)',
    dark: 'linear-gradient(180deg, #0D1B2A 0%, #1B263B 100%)',
    accent: 'linear-gradient(135deg, #00E5FF 0%, #1E88E5 100%)',
    success: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    glass: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
  }
};

const vibetideTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: vibetideColors.primary,
      dark: vibetideColors.primaryDark,
      light: vibetideColors.primaryLight,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: vibetideColors.accent,
      contrastText: '#FFFFFF',
    },
    success: {
      main: vibetideColors.success,
      light: '#34D399',
      dark: '#059669',
    },
    warning: {
      main: vibetideColors.warning,
      light: '#FBBF24',
      dark: '#D97706',
    },
    error: {
      main: vibetideColors.error,
      light: '#F87171',
      dark: '#DC2626',
    },
    info: {
      main: vibetideColors.info,
      light: '#60A5FA',
      dark: '#2563EB',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: vibetideColors.textDark,
      secondary: vibetideColors.textMuted,
    },
    grey: {
      50: '#F8FAFC',
      100: '#F1F5F9',
      200: '#E2E8F0',
      300: '#CBD5E1',
      400: '#94A3B8',
      500: '#64748B',
      600: '#475569',
      700: '#334155',
      800: '#1E293B',
      900: '#0F172A',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.05)',
    '0px 4px 8px rgba(0,0,0,0.08)',
    '0px 8px 16px rgba(0,0,0,0.1)',
    '0px 12px 24px rgba(0,0,0,0.12)',
    '0px 16px 32px rgba(0,0,0,0.15)',
    '0px 20px 40px rgba(0,0,0,0.18)',
    '0px 24px 48px rgba(0,0,0,0.2)',
    '0px 28px 56px rgba(0,0,0,0.22)',
    '0px 32px 64px rgba(0,0,0,0.24)',
    '0px 36px 72px rgba(0,0,0,0.26)',
    '0px 40px 80px rgba(0,0,0,0.28)',
    '0px 44px 88px rgba(0,0,0,0.3)',
    '0px 48px 96px rgba(0,0,0,0.32)',
    '0px 52px 104px rgba(0,0,0,0.34)',
    '0px 56px 112px rgba(0,0,0,0.36)',
    '0px 60px 120px rgba(0,0,0,0.38)',
    '0px 64px 128px rgba(0,0,0,0.4)',
    '0px 68px 136px rgba(0,0,0,0.42)',
    '0px 72px 144px rgba(0,0,0,0.44)',
    '0px 76px 152px rgba(0,0,0,0.46)',
    '0px 80px 160px rgba(0,0,0,0.48)',
    '0px 84px 168px rgba(0,0,0,0.5)',
    '0px 88px 176px rgba(0,0,0,0.52)',
    '0px 92px 184px rgba(0,0,0,0.54)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 24px',
          fontSize: '0.9375rem',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(30, 136, 229, 0.3)',
          },
        },
        contained: {
          background: vibetideColors.gradient.primary,
          '&:hover': {
            background: vibetideColors.gradient.primary,
            filter: 'brightness(1.05)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            background: `${vibetideColors.primary}08`,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 16px rgba(0,0,0,0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
        elevation1: {
          boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
        },
        elevation2: {
          boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
        },
        elevation3: {
          boxShadow: '0px 8px 16px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.2s ease',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: vibetideColors.primary,
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
                borderColor: vibetideColors.primary,
              },
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '4px 8px',
          transition: 'all 0.2s ease',
          '&:hover': {
            background: `${vibetideColors.primary}10`,
          },
          '&.Mui-selected': {
            background: vibetideColors.gradient.primary,
            color: '#FFFFFF',
            '&:hover': {
              background: vibetideColors.gradient.primary,
              filter: 'brightness(1.05)',
            },
            '& .MuiListItemIcon-root': {
              color: '#FFFFFF',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
          background: '#FFFFFF',
          color: vibetideColors.textDark,
        },
      },
    },
  },
});

export default vibetideTheme;

